import React, { useEffect, useMemo, useState } from 'react';
import { BOAPI } from '../service/UsersService';

const GENDER_OPTIONS = [
  { value: '', label: 'הכל' },
  { value: 'זכר', label: 'זכר' },
  { value: 'נקבה', label: 'נקבה' },
];

const defaultForm = () => ({
  title: '',
  body: '',
  mode: 'filtered', // 'filtered' | 'user'
  targetUserId: null,
  targetGender: '',
  targetMinAge: '0',
  targetMaxAge: '100',
  targetBrandIds: '',
});

const formatHistoryTime = (iTime) => {
  if (!iTime) return '';
  const d = new Date(iTime);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const timeStr = d.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  if (isToday) return `היום, ${timeStr}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return `אתמול, ${timeStr}`;
  return `${d.toLocaleDateString('he-IL')}, ${timeStr}`;
};

const describeTarget = (row) => {
  if (row.targetUserId) return `משתמש: ${row.targetUserName || row.targetUserId}`;
  const parts = [];
  if (row.targetGender) parts.push(row.targetGender);
  if (row.targetMinAge != null || row.targetMaxAge != null) {
    parts.push(`גיל ${row.targetMinAge ?? '0'}-${row.targetMaxAge ?? '∞'}`);
  }
  if (row.targetBrandIds) parts.push(`מותג ${row.targetBrandIds}`);
  return parts.length ? parts.join(', ') : 'כולם';
};

const NotificationsTab = () => {
  const [form, setForm] = useState(defaultForm());
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const resp = await BOAPI.fetchNotificationHistory();
      if (resp?.[0]?.[0]?.code === 0) setHistory(resp[1] || []);
    } catch {
      // היסטוריה היא תצוגה משנית — כשל בטעינתה לא צריך לחסום שליחה חדשה
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadUsersIfNeeded = async () => {
    if (users.length > 0) return;
    try {
      const resp = await BOAPI.fetchUsers();
      if (resp?.[0]?.[0]?.code === 0) setUsers(resp[1] || []);
    } catch {
      setError('שגיאה בטעינת רשימת המשתמשים');
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim();
    console.log('Filtering users with query:', users);
    if (!q) return [];
    return users.filter((u) =>
      `${u.fName || ''} ${u.lName || ''}`.includes(q) || (u.username || '').includes(q)
    ).slice(0, 20);
  }, [users, userSearch]);

  const onChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const buildParams = () => {
    const params = { title: form.title.trim(), body: form.body.trim() };
    if (form.mode === 'user') {
      params.targetUserId = form.targetUserId;
    } else {
      params.targetGender = form.targetGender;
      params.targetMinAge = form.targetMinAge;
      params.targetMaxAge = form.targetMaxAge;
      params.targetBrandIds = form.targetBrandIds.trim();
    }
    return params;
  };

  const validate = () => {
    if (!form.title.trim() || !form.body.trim()) return 'נא להזין כותרת ותוכן להודעה';
    if (form.mode === 'user' && !form.targetUserId) return 'נא לבחור משתמש';
    if (form.mode === 'filtered' && form.targetMinAge && form.targetMaxAge && Number(form.targetMinAge) > Number(form.targetMaxAge)) {
      return 'גיל מינימום לא יכול להיות גדול מגיל מקסימום';
    }
    return null;
  };

  const handleSend = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setSuccessMsg('');

    try {
      setSending(true);
      const previewParams = { ...buildParams(), dryRun: '1' };
      const previewResp = await BOAPI.sendNotification(previewParams);
      const previewResult = previewResp?.[0]?.[0];
      if (previewResult?.code !== 0) {
        setError(previewResult?.msg || 'שגיאה בבדיקת קהל היעד');
        return;
      }

      const count = previewResult.recipientCount ?? 0;
      if (count === 0) {
        setError('לא נמצאו משתמשים התואמים את הסינון הזה');
        return;
      }
      if (!window.confirm(`ההודעה תישלח ל-${count} משתמשים. להמשיך?`)) return;

      const sendResp = await BOAPI.sendNotification(buildParams());
      const sendResult = sendResp?.[0]?.[0];
      if (sendResult?.code === 0) {
        setSuccessMsg(`✅ ההודעה נשלחה ל-${sendResult.recipientCount ?? count} משתמשים`);
        setForm(defaultForm());
        setSelectedUser(null);
        setUserSearch('');
        loadHistory();
      } else {
        setError(sendResult?.msg || 'שגיאה בשליחת ההודעה');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בשליחת ההודעה');
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={s.container}>
      <div style={s.headerSection}>
        <h2 style={s.title}>נוטיפיקציות</h2>
      </div>

      {error && (
        <div style={s.error}>
          <span>{error}</span>
          <button style={s.closeErrorButton} onClick={() => setError(null)}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.success}>{successMsg}</div>}

      <div style={s.formCard}>
        <div style={s.formGroup}>
          <label style={s.label}>כותרת *</label>
          <input type="text" value={form.title} onChange={(e) => onChange('title', e.target.value)} style={s.input} placeholder="לדוגמה: עדכון חדש" />
        </div>
        <div style={s.formGroup}>
          <label style={s.label}>תוכן ההודעה *</label>
          <textarea value={form.body} onChange={(e) => onChange('body', e.target.value)} style={s.textarea} rows={3} placeholder="תוכן ההודעה שתופיע למשתמש" />
        </div>

        <div style={s.modeToggle}>
          <button
            style={{ ...s.modeBtn, ...(form.mode === 'filtered' ? s.modeBtnActive : {}) }}
            onClick={() => onChange('mode', 'filtered')}
          >
            לפי סינון (מגדר/גיל/מותג)
          </button>
          <button
            style={{ ...s.modeBtn, ...(form.mode === 'user' ? s.modeBtnActive : {}) }}
            onClick={() => { onChange('mode', 'user'); loadUsersIfNeeded(); }}
          >
            למשתמש ספציפי
          </button>
        </div>

        {form.mode === 'filtered' ? (
          <div style={s.targetSection}>
            <div style={s.formRow}>
              <div style={s.formGroup}>
                <label style={s.label}>מגדר</label>
                <select value={form.targetGender} onChange={(e) => onChange('targetGender', e.target.value)} style={s.input}>
                  {GENDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>גיל מ-</label>
                <input type="number" min={0} value={form.targetMinAge} onChange={(e) => onChange('targetMinAge', e.target.value)} style={s.input} />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>גיל עד</label>
                <input type="number" min={0} value={form.targetMaxAge} onChange={(e) => onChange('targetMaxAge', e.target.value)} style={s.input} />
              </div>
            </div>
            <div style={s.formGroup}>
              <label style={s.label}>מותגים (מזהים מופרדים בפסיק, למשל 1,3)</label>
              <input type="text" value={form.targetBrandIds} onChange={(e) => onChange('targetBrandIds', e.target.value)} style={s.input} placeholder="ריק = כל המותגים" />
            </div>
          </div>
        ) : (
          <div style={s.targetSection}>
            <div style={s.formGroup}>
              <label style={s.label}>חיפוש משתמש</label>
              <input
                type="text"
                value={selectedUser ? `${selectedUser.fName} ${selectedUser.lName}` : userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setSelectedUser(null); onChange('targetUserId', null); }}
                style={s.input}
                placeholder="הקלידו שם או שם משתמש..."
              />
              {!selectedUser && filteredUsers.length > 0 && (
                <div style={s.userResults}>
                  {filteredUsers.map((u) => (
                    <div
                      key={u.id}
                      style={s.userResultRow}
                      onClick={() => { setSelectedUser(u); setUserSearch(''); onChange('targetUserId', u.id); }}
                    >
                      {u.fName} {u.lName} <span style={s.userResultSub}>({u.username})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={s.formButtons}>
          <button style={s.sendButton} onClick={handleSend} disabled={sending}>
            {sending ? 'שולח...' : '📤 שלח הודעה'}
          </button>
        </div>
      </div>

      <h3 style={s.historyTitle}>היסטוריית שליחות</h3>
      {historyLoading ? (
        <div style={s.center}>טוען...</div>
      ) : history.length === 0 ? (
        <div style={s.noData}>עדיין לא נשלחו הודעות</div>
      ) : (
        <div style={s.tableWrapper}>
          <table style={s.table}>
            <thead>
              <tr style={s.headerRow}>
                <th style={s.th}>כותרת</th>
                <th style={s.th}>תוכן</th>
                <th style={s.th}>קהל יעד</th>
                <th style={s.th}>נמענים</th>
                <th style={s.th}>נשלח ע"י</th>
                <th style={s.th}>מתי</th>
              </tr>
            </thead>
            <tbody>
              {history.map((row) => (
                <tr key={row.id} style={s.row}>
                  <td style={s.td}>{row.title}</td>
                  <td style={s.td}>{row.body}</td>
                  <td style={s.td}>{describeTarget(row)}</td>
                  <td style={s.td}>{row.recipientCount}</td>
                  <td style={s.td}>{row.adminName}</td>
                  <td style={s.td}>{formatHistoryTime(row.iTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { padding: '20px', direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerSection: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' },
  title: { margin: 0, color: '#1a1f36', fontSize: '24px', fontWeight: 'bold' },

  formCard: { backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '30px' },
  formGroup: { marginBottom: '15px', position: 'relative' },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '15px' },
  label: { display: 'block', marginBottom: '5px', color: '#333', fontWeight: 'bold', fontSize: '14px' },
  input: { width: '100%', padding: '10px', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'rtl', textAlign: 'right', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '10px', fontFamily: 'Arial, sans-serif', fontSize: '14px', border: '1px solid #ddd', borderRadius: '4px', direction: 'rtl', boxSizing: 'border-box', resize: 'vertical' },

  modeToggle: { display: 'flex', gap: '10px', marginBottom: '15px' },
  modeBtn: { padding: '8px 16px', backgroundColor: '#fff', color: '#697386', border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  modeBtnActive: { backgroundColor: '#1f6feb', color: '#fff', borderColor: '#1f6feb' },

  targetSection: { marginTop: '10px', paddingTop: '15px', borderTop: '1px dashed #ddd' },
  formButtons: { display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '10px' },
  sendButton: { padding: '10px 24px', backgroundColor: '#2e7d32', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },

  userResults: { position: 'absolute', zIndex: 5, top: '100%', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: '220px', overflowY: 'auto' },
  userResultRow: { padding: '10px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: '1px solid #f0f0f0' },
  userResultSub: { color: '#999', fontSize: '12px' },

  historyTitle: { color: '#1a1f36', fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' },
  tableWrapper: { overflowX: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  table: { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' },
  headerRow: { backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' },
  th: { padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#333', fontSize: '14px', borderBottom: '2px solid #ddd' },
  row: { borderBottom: '1px solid #eee' },
  td: { padding: '15px', color: '#666', fontSize: '14px', textAlign: 'right', verticalAlign: 'middle' },

  center: { textAlign: 'center', padding: '40px', color: '#999' },
  error: { padding: '15px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', marginBottom: '20px', fontSize: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeErrorButton: { backgroundColor: 'transparent', color: '#c62828', border: 'none', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' },
  success: { padding: '15px', backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px', marginBottom: '20px', fontSize: '14px' },
  noData: { padding: '40px', textAlign: 'center', color: '#999', backgroundColor: '#f5f5f5', borderRadius: '4px' },
};

export default NotificationsTab;
